import { useState, useMemo } from "react";
import { formatDate } from "@/lib/dateUtils";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { fetchJSON } from "@/lib/api";
import { Trash2, ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight, Search, Copy, FileText, Printer } from "lucide-react";
import { convertNumberToWords } from "@/utils/PrintService";

type Org = { id: string; organization_name: string };
type Statement = {
  id: string;
  statementNo: string;
  statementDate: string;
  organizationName: string;
  fromDate: string;
  toDate: string;
  billAmount: number;
  orgId: string;
};


function fmtINR(n: number) {
  return new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

function formatLocalDate(d: Date) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

type SortKey = "statementNo" | "statementDate" | "organizationName" | "fromDate" | "billAmount";
type SortDir = "asc" | "desc" | null;

const PAGE_SIZE = 10;

export default function StatementGeneration() {
  const today = useMemo(() => formatLocalDate(new Date()), []);
  const queryClient = useQueryClient();

  // Calculate default date range: 1st of current month to today
  const firstOfCurrentMonth = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  }, []);

  // ── Form ─────────────────────────────────────────────────
  const [fromDate, setFromDate] = useState(firstOfCurrentMonth);
  const [toDate, setToDate] = useState(today);
  const [orgId, setOrgId] = useState("");
  const [generating, setGenerating] = useState(false);
  const [formError, setFormError] = useState("");

  const applyPreset = (months: number) => {
    const d = new Date();
    const prior = new Date(d.getFullYear(), d.getMonth() - months, d.getDate());
    const from = formatLocalDate(prior);
    const to = today;

    console.log("applyPreset:", months, "months back => from:", from, "to:", to);
    setFromDate(from);
    setToDate(to);
  };

  // ── Table controls ────────────────────────────────────────
  const [showCount, setShowCount] = useState<"All" | number>("All");
  const [filter, setFilter] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("statementNo");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [page, setPage] = useState(1);

  // ── Delete ────────────────────────────────────────────────
  const [deleteTarget, setDeleteTarget] = useState<Statement | null>(null);
  const [deleting, setDeleting] = useState(false);

  // ── Fetch orgs ────────────────────────────────────────────
  const { data: orgs = [] } = useQuery<Org[]>({
    queryKey: ["/api/credit-customers"],
    queryFn: async () => {
      const res = await fetchJSON("/api/credit-customers");
      const j = await res.json();
      if (!j.ok) throw new Error(j.error);
      return j.rows || [];
    },
  });

  // ── Fetch statements ──────────────────────────────────────
  const { data: statements = [], isLoading } = useQuery<Statement[]>({
    queryKey: ["/api/statements"],
    queryFn: async () => {
      const res = await fetchJSON("/api/statements");
      const j = await res.json();
      if (!j.ok) throw new Error(j.error);
      return (j.rows || []).map((r: any) => ({
        id: r.id || r._id?.toString(),
        statementNo: r.statementNo || "",
        statementDate: (r.statementDate || r.statement_date || "").slice(0, 10),
        organizationName: r.organizationName || r.organization_name || "",
        fromDate: (r.fromDate || r.from_date || "").slice(0, 10),
        toDate: (r.toDate || r.to_date || "").slice(0, 10),
        billAmount: Number(r.billAmount || r.bill_amount || 0),
        orgId: r.orgId || r.org_id || "",
      }));
    },
  });

  const triggerStatementPrint = async (s: Statement, preOpenedWindow?: Window | null) => {
    const printWindow = preOpenedWindow || window.open("", "_blank", "width=1200,height=800,scrollbars=yes,resizable=yes");
    if (!printWindow) {
      alert("Popup blocker active! Please enable popups to print statements.");
      return;
    }
    
    if (!preOpenedWindow) {
      printWindow.document.open();
      printWindow.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Preparing Statement...</title>
        <style>body{font-family:sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;background:#f8f8f8;}
        .msg{text-align:center;color:#555;}</style></head>
        <body><div class="msg"><h3>⏳ Preparing Statement Print...</h3><p>Please wait while we compile the transactions and payments.</p></div></body></html>`);
      printWindow.document.close();
    }

    try {
      const orgRes = await fetchJSON("/api/organization-details");
      const orgJson = await orgRes.json();
      const orgDetails = orgJson.rows?.[0] || {};

      const customerRes = await fetchJSON("/api/credit-customers");
      const customerJson = await customerRes.json();
      const customer = (customerJson.rows || []).find((c: any) => String(c.id) === String(s.orgId));
      if (!customer) throw new Error("Customer details not found");

      const salesRes = await fetchJSON(`/api/credit-sales?customer_id=${s.orgId}`);
      const salesJson = await salesRes.json();
      const allSales: any[] = salesJson?.rows || [];

      const recoveriesRes = await fetchJSON(`/api/recoveries?customer_id=${s.orgId}`);
      const recoveriesJson = await recoveriesRes.json();
      const allRecoveries: any[] = recoveriesJson?.rows || [];

      const templatesRes = await fetchJSON("/api/print-templates");
      const templatesJson = await templatesRes.json();
      const templates = templatesJson.rows || [];
      const activeTemplate = templates.find((t: any) => t.type === "invoice" && t.isActive) || templates[0];

      const fromTime = new Date(s.fromDate + "T00:00:00.000Z").getTime();
      const toTime = new Date(s.toDate + "T23:59:59.999Z").getTime();

      const priorSales = allSales.filter(x => {
        const date = x.saleDate || x.sale_date;
        if (!date) return false;
        return new Date(date).getTime() < fromTime;
      });
      const priorRecoveries = allRecoveries.filter(x => {
        const date = x.recoveryDate || x.recovery_date || x.date;
        if (!date) return false;
        return new Date(date).getTime() < fromTime;
      });

      const openingBalance = Number(customer.opening_balance) || 0;
      const priorSalesTotal = priorSales.reduce((sum, x) => {
        const amt = Math.round(Number(x.totalAmount ?? x.total_amount ?? (Number(x.quantity || 0) * Number(x.pricePerUnit || x.price_per_unit || 0))));
        return sum + amt;
      }, 0);
      const priorRecoveriesTotal = priorRecoveries.reduce((sum, x) => sum + Number(x.received_amount || x.receivedAmount || 0), 0);
      const priorDiscountTotal = priorRecoveries.reduce((sum, x) => sum + Number(x.discount || 0), 0);

      const lastBalance = openingBalance + priorSalesTotal - priorRecoveriesTotal - priorDiscountTotal;

      const periodSales = allSales.filter(x => {
        const date = x.saleDate || x.sale_date;
        if (!date) return false;
        const t = new Date(date).getTime();
        return t >= fromTime && t <= toTime;
      }).sort((a, b) => new Date(a.saleDate || a.sale_date).getTime() - new Date(b.saleDate || b.sale_date).getTime());

      const periodRecoveries = allRecoveries.filter(x => {
        const date = x.recoveryDate || x.recovery_date || x.date;
        if (!date) return false;
        const t = new Date(date).getTime();
        return t >= fromTime && t <= toTime;
      }).sort((a, b) => new Date(a.recoveryDate || a.recovery_date || a.date).getTime() - new Date(b.recoveryDate || b.recovery_date || b.date).getTime());

      const billAmountTotal = periodSales.reduce((sum, x) => {
        const amt = Math.round(Number(x.totalAmount ?? x.total_amount ?? (Number(x.quantity || 0) * Number(x.pricePerUnit || x.price_per_unit || 0))));
        return sum + amt;
      }, 0);
      const miscTotal = periodSales.reduce((sum, x) => sum + Number(x.misc || x.miscCharges || 0), 0);
      const netBillAmount = billAmountTotal + miscTotal;

      const receiptTotal = periodRecoveries.reduce((sum, x) => sum + Number(x.received_amount || x.receivedAmount || 0), 0);
      const balanceAsOnToDate = lastBalance + netBillAmount - receiptTotal;

      const qtyMap: Record<string, number> = {};
      periodSales.forEach(x => {
        const prod = x.product_name || x.fuelProduct || "HSD";
        const qty = Number(x.quantity) || 0;
        qtyMap[prod] = (qtyMap[prod] || 0) + qty;
      });
      const qtySummaryStr = Object.entries(qtyMap)
        .map(([prod, qty]) => `${prod} ${qty.toFixed(2)} LTR`)
        .join(", ");

      const logoMap: Record<string, string> = {
        indianoil: "/logo-indianoil.svg",
        hp: "/logo-hp.svg",
        bpcl: "/logo-bpcl.svg",
        reliance: "/logo-reliance.svg",
        custom: "/brand-logo.png"
      };
      
      let dynamicTemplateHtml = '';
      let dynamicTemplateStyles = '';
      if (activeTemplate) {
        dynamicTemplateStyles = activeTemplate.styles || '';
        const parser = new DOMParser();
        const doc = parser.parseFromString(activeTemplate.htmlStructure || '', 'text/html');
        let combined = '';
        const topGstin = doc.querySelector('.hpbill-top-gstin');
        if (topGstin) combined += topGstin.outerHTML;
        const headerEl = doc.querySelector('.invoice-header, .receipt-header, .hpbill-header');
        if (headerEl) combined += headerEl.outerHTML;

        if (combined) {
          const logoType = activeTemplate.logoType || "custom";
          const logoUrl = logoMap[logoType] || "/brand-logo.png";
          const isCustomLogo = !activeTemplate.logoType || activeTemplate.logoType === "custom";

          const orgNameVal = orgDetails.companyName || orgDetails.company_name || orgDetails.organization_name || "";
          const orgAddrVal = orgDetails.companyAddress || orgDetails.company_address || "";
          const orgGstVal = orgDetails.gstNumber || orgDetails.gst_number || orgDetails.tin_gst_number || "";
          const orgContactVal = orgDetails.contactNumber || orgDetails.contact_number || orgDetails.phone_number || "";

          const compileData: any = {
            logoUrl,
            logoUrlLeft: isCustomLogo ? "/logo-indianoil.svg" : logoUrl,
            logoUrlRight: "",
            logoLeftStyle: "",
            logoRightStyle: "display: none;",
            logoRightContainerStyle: "display: none;",
            orgName: orgNameVal,
            companyName: orgNameVal,
            company_name: orgNameVal,
            orgAddress: orgAddrVal,
            companyAddress: orgAddrVal,
            company_address: orgAddrVal,
            orgGst: orgGstVal,
            gstNumber: orgGstVal,
            gst_number: orgGstVal,
            orgContact: orgContactVal,
            contactNumber: orgContactVal,
            contact_number: orgContactVal,
          };
          
          Object.entries(compileData).forEach(([k, v]) => {
            const placeholder = new RegExp(`{{${k}}}`, "g");
            combined = combined.replace(placeholder, String(v));
          });
          dynamicTemplateHtml = combined;
        }
      }
      const logoUrl = logoMap[activeTemplate?.logoType || "custom"] || "/brand-logo.png";
      const wordsStr = convertNumberToWords(balanceAsOnToDate);

      const content = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Statement - ${s.statementNo}</title>
  <style>
    @media print {
      body { margin: 0; padding: 0; background: #fff; }
      .no-print { display: none !important; }
      @page { size: landscape; margin: 10mm; }
    }
    ${dynamicTemplateStyles}
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      color: #333;
      background: #f3f4f6;
      margin: 0;
      padding: 20px;
    }
    .statement-container {
      max-width: 1200px;
      margin: 0 auto;
      background: #fff;
      padding: 30px;
      border-radius: 12px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
    }
    .header-table {
      width: 100%;
      margin-bottom: 20px;
      border-collapse: collapse;
    }
    .header-table td {
      padding: 4px;
      vertical-align: top;
    }
    .logo {
      width: 60px;
      height: 60px;
      object-fit: contain;
    }
    .pump-title {
      font-size: 20px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      text-align: center;
    }
    .pump-sub {
      font-size: 12px;
      text-align: center;
      color: #4b5563;
      font-weight: 500;
    }
    .info-table {
      width: 100%;
      margin-bottom: 25px;
      border-collapse: collapse;
    }
    .info-table td {
      padding: 6px 8px;
      vertical-align: top;
      font-size: 13px;
    }
    .info-label {
      font-weight: 700;
      color: #4b5563;
      width: 120px;
    }
    .info-value {
      color: #1f2937;
    }
    .data-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 20px;
      font-size: 12px;
    }
    .data-table th {
      background: #e0e7ff;
      color: #1e3a8a;
      font-weight: 700;
      text-transform: uppercase;
      padding: 8px 10px;
      border: 1px solid #c7d2fe;
      text-align: left;
    }
    .data-table td {
      padding: 8px 10px;
      border: 1px solid #e2e8f0;
      color: #374151;
    }
    .data-table tr:nth-child(even) {
      background: #fafafa;
    }
    .summary-table {
      width: 350px;
      margin-left: auto;
      border-collapse: collapse;
      margin-bottom: 25px;
      font-size: 13px;
    }
    .summary-table td {
      padding: 6px 10px;
      border: 1px solid #e2e8f0;
    }
    .summary-label {
      text-align: right;
      font-weight: 600;
      color: #4b5563;
    }
    .summary-val {
      text-align: right;
      font-weight: 700;
      color: #111827;
    }
    .btn-bar {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      margin-bottom: 20px;
      max-width: 1200px;
      margin: 0 auto 15px auto;
    }
    .btn {
      background: #1e3a8a;
      color: #fff;
      border: none;
      border-radius: 6px;
      padding: 8px 16px;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .btn:hover {
      background: #1e40af;
    }
    .words-block {
      font-size: 13px;
      font-weight: 700;
      color: #1f2937;
      margin-bottom: 25px;
      padding: 10px;
      background: #f8fafc;
      border-left: 4px solid #6366f1;
      border-radius: 0 6px 6px 0;
    }
  </style>
</head>
<body>
  <div class="btn-bar no-print">
    <button class="btn" onclick="window.print()">Print Statement</button>
    <button class="btn" style="background:#059669;" onclick="window.close()">Close Window</button>
  </div>
  <div class="statement-container">
    ${dynamicTemplateHtml || `
    <table class="header-table">
      <tr>
        <td style="width: 80px;">
          <img class="logo" src="${logoUrl}" alt="Logo" />
        </td>
        <td>
          <div class="pump-title">${orgDetails.companyName || orgDetails.company_name || orgDetails.organization_name || ""}</div>
          <div class="pump-sub">${orgDetails.companyAddress || orgDetails.company_address || ""}</div>
          <div class="pump-sub">GSTIN: ${orgDetails.gstNumber || orgDetails.gst_number || orgDetails.tin_gst_number || "-"} | Phone: ${orgDetails.contactNumber || orgDetails.contact_number || orgDetails.phone_number || "-"}</div>
        </td>
      </tr>
    </table>
    `}

    <hr style="border: none; border-top: 1px solid #e2e8f0; margin-bottom: 20px;" />

    <table class="info-table">
      <tr>
        <td style="width: 50%;">
          <table style="width: 100%;">
            <tr>
              <td class="info-label">Organization</td>
              <td class="info-value">: ${customer.organization_name || customer.organizationName || "N/A"}</td>
            </tr>
            <tr>
              <td class="info-label">GSTIN</td>
              <td class="info-value">: ${customer.gstin || "-"}</td>
            </tr>
            <tr>
              <td class="info-label">Address</td>
              <td class="info-value">: ${customer.address || "-"}</td>
            </tr>
            <tr>
              <td class="info-label">Phone</td>
              <td class="info-value">: ${customer.mobile_number || "-"}</td>
            </tr>
          </table>
        </td>
        <td style="width: 50%; border-left: 1px solid #f1f5f9;">
          <table style="width: 100%;">
            <tr>
              <td class="info-label">Statement No</td>
              <td class="info-value">: ${s.statementNo}</td>
            </tr>
            <tr>
              <td class="info-label">Statement From</td>
              <td class="info-value">: ${new Date(s.fromDate).toLocaleDateString('en-GB')} To ${new Date(s.toDate).toLocaleDateString('en-GB')}</td>
            </tr>
            <tr>
              <td class="info-label">Date</td>
              <td class="info-value">: ${new Date(s.statementDate).toLocaleDateString('en-GB')}</td>
            </tr>
          </table>
        </td>
      </tr>
    </table>

    <table class="data-table">
      <thead>
        <tr>
          <th style="width: 50px;">Sl.No</th>
          <th>Date</th>
          <th>Indent / Bill No</th>
          <th>Vehicle</th>
          <th>Description</th>
          <th>Item</th>
          <th style="text-align: right;">Quantity</th>
          <th style="text-align: right;">Rate</th>
          <th style="text-align: right;">Misc</th>
          <th style="text-align: right;">Amount</th>
        </tr>
      </thead>
      <tbody>
        ${periodSales.length === 0 ? `
          <tr><td colspan="10" style="text-align: center; color: #94a3b8; padding: 20px;">No credit sales recorded in this period.</td></tr>
        ` : periodSales.map((x, idx) => `
          <tr>
            <td>${idx + 1}</td>
            <td>${new Date(x.saleDate || x.sale_date).toLocaleDateString('en-GB')}</td>
            <td>${x.indentNo || x.indent_no || "-"}/${x.billNo || x.memoNo || x.memo_no || "-"}</td>
            <td>${x.vehicleNo || x.vehicle_number || "-"}</td>
            <td>${x.notes || x.description || "-"}</td>
            <td>${x.product_name || x.fuelProduct || "HSD"}</td>
            <td style="text-align: right;">${Number(x.quantity || 0).toFixed(3)}</td>
            <td style="text-align: right;">${Number(x.pricePerUnit || x.price_per_unit || 0).toFixed(3)}</td>
            <td style="text-align: right;">${Number(x.misc || 0).toFixed(3)}</td>
            <td style="text-align: right;">${Math.round(Number(x.totalAmount ?? x.total_amount ?? (Number(x.quantity || 0) * Number(x.pricePerUnit || x.price_per_unit || 0)))).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
          </tr>
        `).join("")}
      </tbody>
    </table>

    <div style="display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; margin-bottom: 25px;">
      <div class="words-block" style="flex: 1;">
        Amount In words : ${wordsStr}
      </div>
      <table class="summary-table">
        <tr>
          <td class="summary-label">Total Amount</td>
          <td class="summary-val">${Number(billAmountTotal).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        </tr>
        <tr>
          <td class="summary-label">Miscellaneous</td>
          <td class="summary-val">${Number(miscTotal).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        </tr>
        <tr style="background: #f8fafc; font-size: 14px;">
          <td class="summary-label" style="color: #1e3a8a;">Net Bill Amount</td>
          <td class="summary-val" style="color: #1e3a8a;">${Number(netBillAmount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        </tr>
        <tr>
          <td class="summary-label">Last Balance as on ${new Date(s.fromDate).toLocaleDateString('en-GB')}</td>
          <td class="summary-val">${Number(lastBalance).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        </tr>
        <tr>
          <td class="summary-label">Receipt Total</td>
          <td class="summary-val">${Number(receiptTotal).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        </tr>
        <tr style="background: #e0e7ff; font-size: 14px; border-top: 2px solid #6366f1;">
          <td class="summary-label" style="color: #1e3a8a;">Balance as on ${new Date(s.toDate).toLocaleDateString('en-GB')}</td>
          <td class="summary-val" style="color: #1e3a8a;">${Number(balanceAsOnToDate).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        </tr>
      </table>
    </div>

    <div style="font-weight: 700; font-size: 14px; color: #1e3a8a; margin-bottom: 10px;">
      Payments Between ${new Date(s.fromDate).toLocaleDateString('en-GB')} To ${new Date(s.toDate).toLocaleDateString('en-GB')}
    </div>
    <table class="data-table">
      <thead>
        <tr>
          <th>Date</th>
          <th>Type</th>
          <th>Mode</th>
          <th style="text-align: right;">Total Amount</th>
          <th>Total Qty</th>
        </tr>
      </thead>
      <tbody>
        ${periodRecoveries.length === 0 ? `
          <tr>
            <td colspan="4" style="text-align: center; color: #94a3b8; padding: 20px;">No payments recorded in this period.</td>
            <td style="font-weight: bold; color: #1e3a8a; vertical-align: middle; background: #f8fafc; border-left: 2px solid #6366f1; padding: 15px;">${qtySummaryStr || "-"}</td>
          </tr>
        ` : periodRecoveries.map((x, idx) => `
          <tr>
            <td>${new Date(x.recoveryDate || x.recovery_date || x.date).toLocaleDateString('en-GB')}</td>
            <td>${x.type || x.notes || "Receipt"}</td>
            <td>${x.paymentMode || x.mode || "Cash"}</td>
            <td style="text-align: right; font-weight: bold; color: #16a34a;">${Number(x.received_amount || x.amount || 0).toFixed(3)}</td>
            ${idx === 0 ? `<td rowspan="${periodRecoveries.length}" style="font-weight: bold; color: #1e3a8a; vertical-align: middle; background: #f8fafc; border-left: 2px solid #6366f1; padding: 15px;">${qtySummaryStr || "-"}</td>` : ""}
          </tr>
        `).join("")}
      </tbody>
    </table>

    <table style="width: 100%; margin-top: 50px; font-size: 13px;">
      <tr>
        <td style="vertical-align: bottom; width: 60%;">
          <div style="font-weight: bold; color: #4b5563;">Terms:</div>
          <div style="color: #6b7280; font-size: 11px;">Goods once sold can not be taken back or exchanged under any circumstances.</div>
        </td>
        <td style="text-align: right;">
          <div style="font-weight: bold; margin-bottom: 60px; color: #1f2937;">FOR ${orgDetails.companyName || orgDetails.company_name || orgDetails.organization_name || ""}</div>
          <div style="border-top: 1px dashed #94a3b8; display: inline-block; width: 200px; padding-top: 5px; color: #4b5563; font-weight: 600; text-align: center;">Authorised Signatory</div>
        </td>
      </tr>
    </table>
  </div>
</body>
</html>`;

      printWindow.document.open();
      printWindow.document.write(content);
      printWindow.document.close();

      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 500);

    } catch (e: any) {
      console.error(e);
      printWindow.document.open();
      printWindow.document.write(`<!DOCTYPE html><html><body style="font-family:sans-serif;padding:2rem;color:#c00;">
        <h3>⚠️ Preparation Failed</h3>
        <p><strong>${e.message}</strong></p>
      </body></html>`);
      printWindow.document.close();
    }
  };

  // ── Generate ──────────────────────────────────────────────
  const handleGenerate = async () => {
    setFormError("");
    if (!fromDate || !toDate) { setFormError("Please select both From Date and To Date."); return; }
    if (fromDate > toDate) { setFormError("From Date cannot be after To Date."); return; }
    if (!orgId) { setFormError("Please select an Organization."); return; }

    const printWin = window.open("", "_blank", "width=1200,height=800,scrollbars=yes,resizable=yes");
    if (printWin) {
      printWin.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Preparing Statement...</title>
        <style>body{font-family:sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;background:#f8f8f8;}
        .msg{text-align:center;color:#555;}</style></head>
        <body><div class="msg"><h3>⏳ Preparing Statement Print...</h3><p>Please wait while we compile the transactions and payments.</p></div></body></html>`);
      printWin.document.close();
    }

    setGenerating(true);
    try {
      // Compute bill amount from credit sales
      const params = new URLSearchParams({ customer_id: orgId, from_date: fromDate, to_date: toDate });
      const salesRes = await fetchJSON(`/api/credit-sales?${params}`);
      const salesJson = await salesRes.json();
      const salesRows: any[] = salesJson?.ok ? salesJson.rows || [] : [];
      const billAmount = salesRows
        .filter((s: any) => String(s.creditCustomerId || s.credit_customer_id || "") === String(orgId))
        .reduce((sum: number, s: any) => sum + Number(s.total_amount || 0), 0);

      const org = orgs.find((o) => o.id === orgId);
      const saveRes = await fetchJSON("/api/statements", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          org_id: orgId,
          organization_name: org?.organization_name || "",
          statement_date: today,       // auto = today
          from_date: fromDate,
          to_date: toDate,
          bill_amount: billAmount,
        }),
      });
      const saveJson = await saveRes.json();
      if (!saveJson.ok) throw new Error(saveJson.error || "Failed to save statement");
      queryClient.invalidateQueries({ queryKey: ["/api/statements"] });
      // Keep dates selected so user doesn't have to re-enter them for multiple organizations
      setOrgId("");

      const newStatement: Statement = {
        id: saveJson.id,
        statementNo: saveJson.statementNo,
        statementDate: today,
        organizationName: org?.organization_name || "",
        fromDate: fromDate,
        toDate: toDate,
        billAmount: billAmount,
        orgId: orgId
      };
      
      triggerStatementPrint(newStatement, printWin);
    } catch (err: any) {
      if (printWin) printWin.close();
      setFormError(err.message || "Failed to generate statement.");
    } finally {
      setGenerating(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetchJSON(`/api/statements/${deleteTarget.id}`, { method: "DELETE" });
      const j = await res.json();
      if (!j.ok) throw new Error(j.error || "Failed");
      queryClient.invalidateQueries({ queryKey: ["/api/statements"] });
      setDeleteTarget(null);
    } catch (err: any) {
      alert(err.message || "Failed to delete.");
    } finally {
      setDeleting(false);
    }
  };

  // ── Sort helper ───────────────────────────────────────────
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : d === "desc" ? null : "asc"));
    } else {
      setSortKey(key); setSortDir("asc");
    }
    setPage(1);
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col || sortDir === null) return <ChevronsUpDown className="h-3 w-3 opacity-40 ml-1 inline" />;
    return sortDir === "asc"
      ? <ChevronUp className="h-3 w-3 ml-1 inline text-indigo-600" />
      : <ChevronDown className="h-3 w-3 ml-1 inline text-indigo-600" />;
  };

  // ── Filtered + sorted + paginated ─────────────────────────
  const filtered = useMemo(() => {
    const q = filter.toLowerCase();
    return statements.filter((s) =>
      !q ||
      s.statementNo.toLowerCase().includes(q) ||
      s.organizationName.toLowerCase().includes(q) ||
      formatDate(s.statementDate).includes(q) ||
      formatDate(s.fromDate).includes(q) ||
      formatDate(s.toDate).includes(q)
    );
  }, [statements, filter]);

  const sorted = useMemo(() => {
    if (!sortDir) return filtered;
    return [...filtered].sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      const cmp = typeof av === "number"
        ? av - (bv as number)
        : String(av).localeCompare(String(bv));
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filtered, sortKey, sortDir]);

  const pageSize = showCount === "All" ? sorted.length || 1 : showCount;
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const paginated = showCount === "All" ? sorted : sorted.slice((page - 1) * pageSize, page * pageSize);

  // ── Copy to clipboard ─────────────────────────────────────
  const handleCopy = () => {
    const text = sorted.map(s =>
      `${s.statementNo}\t${formatDate(s.statementDate)}\t${s.organizationName}\t${formatDate(s.fromDate)} - ${formatDate(s.toDate)}\t${fmtINR(s.billAmount)}`
    ).join("\n");
    navigator.clipboard.writeText(text).catch(() => {});
  };

  const handleCSV = () => {
    const header = "ST NO.,Statement Date,Organization,From Date - To Date,Bill Amount";
    const rows = sorted.map(s =>
      `${s.statementNo},${formatDate(s.statementDate)},${s.organizationName},${formatDate(s.fromDate)} - ${formatDate(s.toDate)},${s.billAmount}`
    );
    const blob = new Blob([[header, ...rows].join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = "statements.csv"; a.click();
  };

  const handlePrint = () => window.print();

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="space-y-4">

      {/* ── Blue generation banner ── */}
      <div className="rounded-xl bg-gradient-to-r from-[#3b5bdb] to-[#4c6ef5] p-5 shadow-md">
        <h2 className="text-white font-bold text-lg mb-4">Generation</h2>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-white/90 text-sm font-semibold whitespace-nowrap">Date Range</span>
          <input
            type="date"
            value={fromDate}
            max={today}
            onChange={(e) => setFromDate(e.target.value)}
            placeholder="From Date"
            className="h-9 px-3 rounded-md text-sm font-medium bg-white text-slate-800 border-0 outline-none focus:ring-2 focus:ring-white/50 w-40"
          />
          <span className="text-white/80 text-sm font-semibold">To</span>
          <input
            type="date"
            value={toDate}
            max={today}
            onChange={(e) => setToDate(e.target.value)}
            placeholder="To Date"
            className="h-9 px-3 rounded-md text-sm font-medium bg-white text-slate-800 border-0 outline-none focus:ring-2 focus:ring-white/50 w-40"
          />
          <span className="text-white/90 text-sm font-semibold whitespace-nowrap">Organization</span>
          <select
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
            className="h-9 px-3 rounded-md text-sm font-medium bg-white text-slate-800 border-0 outline-none focus:ring-2 focus:ring-white/50 min-w-[200px]"
          >
            <option value="">Choose Organization</option>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>{o.organization_name}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={generating}
            className="h-9 px-5 rounded-md bg-[#1a237e] hover:bg-[#283593] text-white text-sm font-bold transition-colors disabled:opacity-60 flex items-center gap-2 shadow"
          >
            {generating ? (
              <><span className="h-3.5 w-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Generating…</>
            ) : "Generate"}
          </button>
        </div>

        {/* Quick Ranges */}
        <div className="flex flex-wrap gap-2 mt-4 items-center">
          <span className="text-white/70 text-xs font-semibold uppercase tracking-wider mr-1">Quick Ranges:</span>
          <button
            type="button"
            onClick={() => applyPreset(1)}
            className="px-3 py-1 text-xs rounded bg-white/10 hover:bg-white/20 text-white font-semibold transition-all border border-white/10 shadow-sm"
          >
            1 Month
          </button>
          <button
            type="button"
            onClick={() => applyPreset(3)}
            className="px-3 py-1 text-xs rounded bg-white/10 hover:bg-white/20 text-white font-semibold transition-all border border-white/10 shadow-sm"
          >
            3 Month
          </button>
          <button
            type="button"
            onClick={() => applyPreset(6)}
            className="px-3 py-1 text-xs rounded bg-white/10 hover:bg-white/20 text-white font-semibold transition-all border border-white/10 shadow-sm"
          >
            6 Month
          </button>
          <button
            type="button"
            onClick={() => applyPreset(12)}
            className="px-3 py-1 text-xs rounded bg-white/10 hover:bg-white/20 text-white font-semibold transition-all border border-white/10 shadow-sm"
          >
            1 Year
          </button>
        </div>

        {formError && (
          <p className="mt-3 text-yellow-200 text-xs font-semibold bg-white/10 rounded px-3 py-1.5 w-fit">
            ⚠ {formError}
          </p>
        )}
      </div>

      {/* ── Table card ── */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">

        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-100">
          {/* Show dropdown */}
          <div className="flex items-center gap-2 text-sm text-slate-600 font-medium">
            <span>Show:</span>
            <select
              value={showCount}
              onChange={(e) => {
                const v = e.target.value;
                setShowCount(v === "All" ? "All" : Number(v));
                setPage(1);
              }}
              className="border border-slate-200 rounded-md px-2 py-1 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-indigo-400"
            >
              <option value="All">All</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>

          {/* Right: Export buttons + filter */}
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={handleCopy}  className="border border-indigo-400 text-indigo-700 hover:bg-indigo-50 text-xs font-bold px-3 py-1.5 rounded transition-colors flex items-center gap-1.5"><Copy className="h-3.5 w-3.5" />Copy</button>
            <button onClick={handleCSV}   className="border border-emerald-500 text-emerald-700 hover:bg-emerald-50 text-xs font-bold px-3 py-1.5 rounded transition-colors flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" />CSV</button>
            <button className="border border-red-400 text-red-600 hover:bg-red-50 text-xs font-bold px-3 py-1.5 rounded transition-colors">PDF</button>
            <button onClick={handlePrint} className="border border-sky-400 text-sky-700 hover:bg-sky-50 text-xs font-bold px-3 py-1.5 rounded transition-colors flex items-center gap-1.5"><Printer className="h-3.5 w-3.5" />Print</button>
            <div className="flex items-center gap-1.5 border border-slate-200 rounded px-2 py-1.5 bg-slate-50">
              <Search className="h-3.5 w-3.5 text-slate-400" />
              <input
                type="text"
                placeholder="Type to filter..."
                value={filter}
                onChange={(e) => { setFilter(e.target.value); setPage(1); }}
                className="text-xs bg-transparent outline-none w-36 text-slate-700 placeholder:text-slate-400"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/60">
                {[
                  { key: "statementNo" as SortKey, label: "ST NO." },
                  { key: "statementDate" as SortKey, label: "Statement Date" },
                  { key: "organizationName" as SortKey, label: "Organization" },
                  { key: "fromDate" as SortKey, label: "From Date - To Date" },
                  { key: "billAmount" as SortKey, label: "Bill Amount" },
                ].map(({ key, label }) => (
                  <th
                    key={key}
                    onClick={() => handleSort(key)}
                    className="text-left text-xs font-bold text-slate-600 uppercase tracking-wide px-4 py-3 cursor-pointer select-none hover:text-indigo-700 whitespace-nowrap"
                  >
                    {label}<SortIcon col={key} />
                  </th>
                ))}
                <th className="text-left text-xs font-bold text-slate-600 uppercase tracking-wide px-4 py-3 whitespace-nowrap">
                  Action <ChevronsUpDown className="h-3 w-3 opacity-40 ml-1 inline" />
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="text-center py-16 text-slate-400 text-sm">
                    <span className="flex items-center justify-center gap-2">
                      <span className="h-4 w-4 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin" />
                      Loading…
                    </span>
                  </td>
                </tr>
              ) : paginated.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-16 text-slate-400 text-sm">
                    No statements found. Generate one using the form above.
                  </td>
                </tr>
              ) : (
                paginated.map((s, idx) => (
                  <tr
                    key={s.id}
                    className={`border-b border-slate-50 hover:bg-blue-50/30 transition-colors ${idx % 2 === 1 ? "bg-slate-50/40" : ""}`}
                  >
                    <td className="px-4 py-3">
                      <span
                        onClick={() => triggerStatementPrint(s)}
                        className="text-blue-600 font-bold text-sm cursor-pointer hover:underline flex items-center gap-1.5"
                      >
                        <Printer className="h-3.5 w-3.5" />
                        {s.statementNo}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-700 font-medium text-sm">{formatDate(s.statementDate)}</td>
                    <td className="px-4 py-3 text-slate-800 font-semibold text-sm">{s.organizationName}</td>
                    <td className="px-4 py-3 text-slate-600 font-medium text-sm tabular-nums">
                      {formatDate(s.fromDate)} - {formatDate(s.toDate)}
                    </td>
                    <td className="px-4 py-3 text-slate-800 font-bold text-sm tabular-nums">
                      {fmtINR(s.billAmount)}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setDeleteTarget(s)}
                        title="Delete"
                        className="p-2 rounded-lg hover:bg-rose-50 text-rose-500 hover:text-rose-700 transition-colors group"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer: count + pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-xs text-slate-500 font-medium">
          <span>
            {sorted.length === 0
              ? "No entries"
              : showCount === "All"
              ? `Showing 1 to ${sorted.length} of ${sorted.length} entries`
              : `Showing ${Math.min((page - 1) * pageSize + 1, sorted.length)} to ${Math.min(page * pageSize, sorted.length)} of ${sorted.length} entries`}
          </span>
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="w-7 h-7 flex items-center justify-center rounded border border-slate-200 hover:border-indigo-400 disabled:opacity-40 transition-colors"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                .map((p, idx, arr) => (
                  <>
                    {idx > 0 && arr[idx - 1] !== p - 1 && (
                      <span key={`ellipsis-${p}`} className="px-1 text-slate-400">…</span>
                    )}
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`w-7 h-7 flex items-center justify-center rounded text-xs font-bold border transition-colors ${
                        p === page
                          ? "bg-indigo-600 text-white border-indigo-600"
                          : "border-slate-200 hover:border-indigo-400 text-slate-600"
                      }`}
                    >
                      {p}
                    </button>
                  </>
                ))}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="w-7 h-7 flex items-center justify-center rounded border border-slate-200 hover:border-indigo-400 disabled:opacity-40 transition-colors"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Delete confirm dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Statement?</AlertDialogTitle>
            <AlertDialogDescription>
              Delete statement <strong>{deleteTarget?.statementNo}</strong> for{" "}
              <strong>{deleteTarget?.organizationName}</strong>? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {deleting ? "Deleting…" : "Yes, Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

